import type { Socket, SocketId } from "./types";
import type { JSONSchema7 } from "json-schema";

export interface SocketDefinition {
  id: SocketId;
  schema: JSONSchema7;
  type?: string;
  multiple?: boolean;
  required?: boolean;
  metadata?: Record<string, unknown>;
}

export interface NodeTypeDefinition {
  inputs: SocketDefinition[];
  outputs: SocketDefinition[];
  parameters?: ParameterDefinition[];
  metadata?: Record<string, unknown>;
}

export interface ParameterDefinition {
  id: string;
  schema: JSONSchema7;
  defaultValue?: unknown;
  metadata?: Record<string, unknown>;
}

export class NodeTypeRegistry {
  private definitions = new Map<string, NodeTypeDefinition>();

  /**
   * Register a node type definition
   */
  register(type: string, definition: NodeTypeDefinition): void {
    if (this.definitions.has(type)) {
      console.warn(`Overwriting existing node type definition: ${type}`);
    }
    this.definitions.set(type, definition);
  }

  /**
   * Get a node type definition
   */
  getDefinition(type: string): NodeTypeDefinition | undefined {
    return this.definitions.get(type);
  }

  /**
   * Check if a type is registered
   */
  hasType(type: string): boolean {
    return this.definitions.has(type);
  }

  /**
   * Get all registered types
   */
  getTypes(): string[] {
    return Array.from(this.definitions.keys());
  }

  /**
   * Clear all definitions
   */
  clear(): void {
    this.definitions.clear();
  }

  /**
   * Register common node types for testing
   */
  registerCommonTypes(): void {
    // Basic processor node
    this.register("processor", {
      inputs: [
        {
          id: "in",
          schema: { type: "object" },
          multiple: false,
        },
      ],
      outputs: [
        {
          id: "out",
          schema: { type: "object" },
        },
      ],
    });

    // Source node (only outputs)
    this.register("source", {
      inputs: [],
      outputs: [
        {
          id: "value",
          schema: { type: "object" },
        },
      ],
    });

    // Sink node (only inputs)
    this.register("sink", {
      inputs: [
        {
          id: "result",
          schema: { type: "object" },
          multiple: false,
        },
      ],
      outputs: [],
    });

    // Merge node (multiple inputs)
    this.register("merge", {
      inputs: [
        {
          id: "in",
          schema: { type: "object" },
          multiple: true,
        },
      ],
      outputs: [
        {
          id: "out",
          schema: { type: "object" },
        },
      ],
    });

    // Input node for subgraphs
    this.register("graph-input", {
      inputs: [],
      outputs: [
        {
          id: "value",
          schema: { type: "object" },
        },
      ],
    });

    // Output node for subgraphs
    this.register("graph-output", {
      inputs: [
        {
          id: "value",
          schema: { type: "object" },
        },
      ],
      outputs: [],
    });
  }
}
